/**
 * The emote table (ADR-114 decisions 1.1, 1.2, 3.1 and 7.2). An emote is an emoji, a picture shown above its sender's
 * character, or a roleplay action, a line of Local chat the server writes from this table. The page sends an emote's
 * kind and id and never text; the map server composes an action's sentence from its own copy of the table, and each
 * page draws the wheel from the same rows.
 *
 * Ids are per kind, each kind numbered from 1, so an emote is named by its kind and its id together, as the wire names
 * it. Within a kind an id is never reused or renumbered: a row is only ever added at the end. A change to a sentence's
 * wording is a change to this data and to the golden in the test beside it.
 */

/**
 * The two kinds of emote, as the wire's `emote` names them.
 */
export const EMOTE_KINDS = ["emoji", "action"] as const;

/**
 * One of `EMOTE_KINDS`.
 */
export type EmoteKind = (typeof EMOTE_KINDS)[number];

/**
 * The greatest id an emote of either kind may have: the wire carries an id as a whole number from 1 to this.
 */
export const MAX_EMOTE_ID = 65_535;

/**
 * The wheel's page of emoji, its first.
 */
export const EMOJI_PAGE = "Emoji";

/**
 * The wheel's pages of actions, Bible 9.4's five groups in its order.
 */
export const ACTION_PAGES = ["Greetings", "Emotions", "Actions", "Interactions", "Duel"] as const;

/**
 * A page of actions.
 */
export type ActionPage = (typeof ACTION_PAGES)[number];

/**
 * Every page of the wheel in the order Q and E turn them.
 */
export const EMOTE_PAGES = [EMOJI_PAGE, ...ACTION_PAGES] as const;

/**
 * A page of the wheel.
 */
export type EmotePage = (typeof EMOTE_PAGES)[number];

/**
 * The set every account holds (ADR-114 decision 6.3).
 */
export const BASE_EMOTE_SET = "base";

/**
 * Where the target's name goes in an action's targeted sentence.
 */
export const TARGET_PLACEHOLDER = "{target}";

/**
 * A roleplay action. Its two sentences are the part after the sender's name, which each page writes itself: the
 * `targeted` one holds `TARGET_PLACEHOLDER` exactly once, and the `general` one, sent with no one selected, names no
 * target. Both are authored, since English differs by verb, and neither is built by a rule. `emoji` is the icon key of
 * the picture shown above the sender while the line goes to chat (ADR-114's amendment of 2026-10-09); several actions
 * may share one, and an emoji emote may name it too.
 */
export interface EmoteAction {
  readonly kind: "action";
  readonly id: number;
  readonly word: string;
  readonly page: ActionPage;
  readonly set: string;
  readonly targeted: string;
  readonly general: string;
  readonly emoji: string;
}

/**
 * An emoji: its name, read out by assistive technology and shown in the wheel's footer, and the key of its art in the
 * icon table (ADR-111).
 */
export interface EmoteEmoji {
  readonly kind: "emoji";
  readonly id: number;
  readonly name: string;
  readonly key: string;
  readonly page: typeof EMOJI_PAGE;
  readonly set: string;
}

/**
 * An emote of either kind.
 */
export type Emote = EmoteAction | EmoteEmoji;

/**
 * A row of the base set's actions.
 *
 * @param id The action's id, its place among the actions from 1.
 * @param page The page it is on.
 * @param word The word on its button.
 * @param targeted The sentence with someone selected, holding `TARGET_PLACEHOLDER` once.
 * @param general The sentence with no one selected.
 * @param emoji The icon key of the picture shown above the sender.
 * @returns The frozen row.
 */
function action(id: number, page: ActionPage, word: string, targeted: string, general: string, emoji: string): EmoteAction {
  return Object.freeze({ kind: "action", id, word, page, set: BASE_EMOTE_SET, targeted, general, emoji });
}

/**
 * A row of the base set's emoji.
 *
 * @param id The emoji's id, its place among the emoji from 1.
 * @param name Its name.
 * @param key Its icon key.
 * @returns The frozen row.
 */
function emoji(id: number, name: string, key: string): EmoteEmoji {
  return Object.freeze({ kind: "emoji", id, name, key, page: EMOJI_PAGE, set: BASE_EMOTE_SET });
}

/**
 * The actions in id order: Bible 9.4's eighteen and the six added (Cheer, Shrug, Kneel, Yawn, Stare and Threaten),
 * each sentence as ADR-114 decision 3.1 drafts it, each picture as the author approved it on 2026-10-08.
 */
export const EMOTE_ACTIONS: readonly EmoteAction[] = Object.freeze([
  action(1, "Greetings", "Wave", "waves at {target}.", "waves to everyone.", "emoji-happy"),
  action(2, "Greetings", "Bow", "bows to {target}.", "bows to those around.", "emoji-content"),
  action(3, "Greetings", "Salute", "salutes {target}.", "salutes all present.", "emoji-sunglasses"),
  action(4, "Greetings", "Nod", "nods at {target}.", "nods in agreement.", "emoji-smiling-eyes"),
  action(5, "Emotions", "Laugh", "laughs at {target}.", "laughs out loud.", "emoji-laughing"),
  action(6, "Emotions", "Cry", "cries in front of {target}.", "bursts into tears.", "emoji-crying"),
  action(7, "Emotions", "Angry", "glares angrily at {target}.", "fumes with anger.", "emoji-angry"),
  action(8, "Emotions", "Confused", "looks at {target} in confusion.", "looks around in confusion.", "emoji-dizzy"),
  action(9, "Emotions", "Cheer", "cheers for {target}.", "cheers loudly.", "emoji-party"),
  action(10, "Emotions", "Shrug", "shrugs at {target}.", "shrugs at it all.", "emoji-expressionless"),
  action(11, "Actions", "Sit", "sits down beside {target}.", "sits down for a rest.", "emoji-content"),
  action(12, "Actions", "Dance", "dances with {target}.", "dances on the spot.", "emoji-star-eyes"),
  action(13, "Actions", "Meditate", "meditates beside {target}.", "settles down to meditate.", "emoji-halo"),
  action(14, "Actions", "Stretch", "stretches in front of {target}.", "stretches out stiff limbs.", "emoji-squinting"),
  action(15, "Actions", "Kneel", "kneels before {target}.", "kneels on the ground.", "emoji-blushing"),
  action(16, "Actions", "Yawn", "yawns at {target}.", "yawns widely.", "emoji-open-mouth"),
  action(17, "Interactions", "Point", "points at {target}.", "points into the distance.", "emoji-point-right"),
  action(18, "Interactions", "Beckon", "beckons {target} over.", "beckons everyone closer.", "emoji-point-up"),
  action(19, "Interactions", "Applaud", "applauds {target}.", "applauds everyone here.", "emoji-grinning"),
  action(20, "Interactions", "Toast", "raises a toast to {target}.", "raises a toast to everyone.", "emoji-beaming"),
  action(21, "Interactions", "Stare", "stares at {target}.", "stares into the distance.", "emoji-eyes"),
  action(22, "Interactions", "Threaten", "threatens {target}.", "threatens everyone nearby.", "emoji-swearing"),
  action(23, "Duel", "Ready stance", "takes a ready stance facing {target}.", "takes a ready stance.", "emoji-raised-eyebrow"),
  action(24, "Duel", "Challenge gesture", "makes a challenging gesture at {target}.", "makes a challenging gesture at all comers.", "emoji-devil"),
]);

/**
 * The emoji in id order: the twelve of ADR-114 decision 7.2.
 */
export const EMOTE_EMOJI: readonly EmoteEmoji[] = Object.freeze([
  emoji(1, "Happy", "emoji-happy"),
  emoji(2, "Laughing", "emoji-laughing"),
  emoji(3, "Sad", "emoji-sad"),
  emoji(4, "Crying", "emoji-crying"),
  emoji(5, "Angry", "emoji-angry"),
  emoji(6, "Surprised", "emoji-surprised"),
  emoji(7, "Love", "emoji-love"),
  emoji(8, "Thinking", "emoji-thinking"),
  emoji(9, "Sleepy", "emoji-sleepy"),
  emoji(10, "Sick", "emoji-sick"),
  emoji(11, "Thumbs up", "emoji-thumbs-up"),
  emoji(12, "Thumbs down", "emoji-thumbs-down"),
]);

const ACTIONS_BY_ID: ReadonlyMap<number, EmoteAction> = new Map(EMOTE_ACTIONS.map((row) => [row.id, row]));
const EMOJI_BY_ID: ReadonlyMap<number, EmoteEmoji> = new Map(EMOTE_EMOJI.map((row) => [row.id, row]));

/**
 * Looks an action up by its id among the actions.
 *
 * @param id The id, as a client sent it or a table names it.
 * @returns The action, or undefined for an id no action has, an emoji's id among them.
 * @example
 * emoteAction(22)?.word;
 * // "Threaten"
 */
export function emoteAction(id: number): EmoteAction | undefined {
  return ACTIONS_BY_ID.get(id);
}

/**
 * Looks an emoji up by its id among the emoji.
 *
 * @param id The id, as a client or a server sent it.
 * @returns The emoji, or undefined for an id no emoji has.
 * @example
 * emoteEmoji(1)?.key;
 * // "emoji-happy"
 */
export function emoteEmoji(id: number): EmoteEmoji | undefined {
  return EMOJI_BY_ID.get(id);
}
